import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";

const NotificationContext = createContext(null);

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timeouts = useRef([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const notify = useCallback((type, message) => {
    const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);

    setNotifications(prev => [...prev, { id, type, message }]);

    const timeout = setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);

    timeouts.current.push(timeout);
  }, []);

  useEffect(() => {
    return () => {
      timeouts.current.forEach(clearTimeout);
      timeouts.current = [];
    };
  }, []);

  const value = useMemo(() => ({ notify, removeNotification }), [notify, removeNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Notification Container */}
      <div
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 2147483997,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          minWidth: "380px",
          fontFamily: "Inter, Roboto, sans-serif",
        }}
      >
        {notifications.map(n => {
          const isSuccess = n.type === "success";
          const color = isSuccess ? "#22c55e" : "#ef4444";
          const title = isSuccess ? "Success" : "Error";
          const icon = isSuccess ? "✔" : "✖";

          return (
            <div
              key={n.id}
              role="alert"
              className="fade-in"
              style={{
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                transition: "all 0.3s ease",
                borderLeft: `8px solid ${color}`,
                borderTop: `2px solid ${color}`,
                borderBottom: `2px solid ${color}`,
              }}
            >
              <div
                style={{
                  background: color,
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                {icon}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "16px", color: "#111827" }}>
                  {title}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>{n.message}</div>
              </div>

              <button
                onClick={() => removeNotification(n.id)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "18px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        .fade-in { animation: fadeInRight 0.35s ease forwards; }
        @keyframes fadeInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </NotificationContext.Provider>
  );
};
